import { useCallback, useEffect, useRef } from 'react'
import { doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore'
import { db } from '../config/firebase.js'
import { useAuth } from './useAuth.js'

/**
 * Firebase v11+ Device Access Tracking Hook
 * 
 * Efficiently tracks user's last access time for devices with optimized re-renders
 * and Firebase v11+ patterns. Uses debouncing and ref-based optimizations for performance.
 * 
 * Features:
 * - React 19+ optimized with minimal re-renders
 * - Firebase v11+ server timestamps
 * - Automatic debouncing to prevent excessive updates
 * - Silent error handling to maintain UX
 * - Ref-based optimization to avoid effect re-runs
 * 
 * @param {string} deviceId - Device serial number/ID to track
 * @param {boolean} shouldTrack - Enable/disable tracking (default: true)
 * @returns {Object} Object containing updateLastAccess function
 * @returns {Function} updateLastAccess - Manually trigger access update
 */
export function useDeviceAccessTracking(deviceId, shouldTrack = true) {
  const { user } = useAuth()
  const lastUpdateRef = useRef(0)
  const timeoutRef = useRef(null)
  
  // Firebase v11+ optimized access tracking with throttling
  const updateLastAccess = useCallback(async () => {
    if (!user?.uid || !deviceId) return
    
    // Skip access tracking for sysadmins on devices they're not registered on
    // Sysadmins have global access and don't need individual device tracking
    try {
      const userRef = doc(db, 'users', user.uid)
      const userDoc = await getDoc(userRef)
      const isSysAdmin = userDoc.exists() && userDoc.data().isSysAdmin === true
      
      if (isSysAdmin) {
        // Sysadmins don't need access tracking - they have global access
        return
      }
    } catch (_error) {
      // If we can't check sysadmin status, proceed with normal tracking
    }
    
    // Throttle updates to once per minute to reduce Firestore writes
    const now = Date.now()
    const THROTTLE_INTERVAL = 60_000 // 1 minute
    
    if (now - lastUpdateRef.current < THROTTLE_INTERVAL) return
    
    try {
      lastUpdateRef.current = now
      const userDeviceRef = doc(db, `userDevices/${user.uid}/devices/${deviceId}`)
      await updateDoc(userDeviceRef, {
        lastAccessedAt: serverTimestamp(),
      })
    } catch (_error) {
      // Silent failure for access tracking - maintain UX
    }
  }, [user?.uid, deviceId])

  // React 19+ optimized auto-tracking with cleanup
  useEffect(() => {
    if (!shouldTrack || !user?.uid || !deviceId) return
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    
    // Debounced tracking to avoid immediate writes on component mount
    timeoutRef.current = setTimeout(updateLastAccess, 2000)
    
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [shouldTrack, user?.uid, deviceId, updateLastAccess])

  return { updateLastAccess }
}
